import React, { useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import countries from "../../constant/countries";
import "react-toastify/dist/ReactToastify.css";
import { Upload } from "lucide-react";

const CategoryAdverPage = () => {
  const navigate = useNavigate();
  const { countryName, category } = useParams();
  const location = useLocation();

  const stateCountries = location.state?.selectedCountries || [];

  const selectedCountries =
    stateCountries.length > 0
      ? stateCountries
      : [countryName.replace(/-/g, " ")];

  const matchedCountries = selectedCountries
    .map((name) =>
      countries.find(
        (country) =>
          country.name.toLowerCase().replace(/\s+/g, "-") ===
          name.toLowerCase().replace(/\s+/g, "-")
      )
    )
    .filter(Boolean); // removes undefined if not matched

  const [formData, setFormData] = useState({
    full_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    city: "",
    comments: "",
    file: null,
    category: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("Please upload an ad file.");
      return;
    }

    const submissionData = new FormData();
    submissionData.append("full_name", formData.full_name);
    submissionData.append("last_name", formData.last_name);
    submissionData.append("email", formData.email);
    submissionData.append("phone_number", formData.phone_number);
    submissionData.append("city", formData.city);
    submissionData.append("comments", formData.comments);
    submissionData.append("file", formData.file);
    submissionData.append("category", formData.category);
    submissionData.append(
      "countries",
      matchedCountries.map((c) => c.name).join(", ")
    );

    try {
      const response = await fetch("http://localhost:5000/api/upload", {
        method: "POST",
        body: submissionData,
      });

      if (response.ok) {
        toast.success("Form submitted successfully!");
        setTimeout(() => {
          navigate("/payment-form");
        }, 1000); // optional delay for toast to show

        setFormData({
          full_name: "",
          last_name: "",
          email: "",
          phone_number: "",
          city: "",
          comments: "",
          file: null,
          category: "",
        });
      } else {
        toast.error("Failed to submit the form.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred while submitting the form.");
    }
  };

  return (
    <>
      <section
        className="relative text-white min-h-[75vh] flex items-center justify-center overflow-hidden"
        style={{
          background:
            "linear-gradient(90deg,rgba(252, 53, 236, 1) 0%, rgba(237, 0, 170, 1) 100%)",
        }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1616692474781-9ef0114c15b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')",
          }}
        ></div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl">
          <h1 className="text-4xl uppercase sm:text-5xl md:text-6xl font-extrabold tracking-tight font-[Aeonik]">
            Advertising Portal
          </h1>
          <h1 className="text-4xl mt-2 sm:text-5xl md:text-6xl font-extrabold tracking-tight font-[Aeonik]">
            <span className="text-black">{category?.toUpperCase()}</span>
          </h1>

          <div className="mt-6 flex items-center justify-center gap-6 flex-wrap text-white text-xl">
            {matchedCountries.length === countries.length ? (
              <div className="bg-white/20 px-6 py-3 rounded-lg text-white text-xl font-semibold">
                All Countries Selected
              </div>
            ) : (
              matchedCountries.map((c) => (
                <div
                  key={c.name}
                  className="flex items-center gap-2 bg-white/20 px-4 py-1 rounded-lg"
                >
                  <img
                    src={c.flag}
                    alt={c.name}
                    className="w-6 h-4 rounded shadow"
                  />
                  <span className="font-semibold">{c.name}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <div className="min-h-screen px-4 py-10 bg-gradient-to-b from-white to-gray-100 text-gray-800">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl text-center font-bold text-[#ED00AA]">
            Advertising Form
          </h1>

          <p className="text-gray-600 mt-4 mb-4 font-bold text-2xl text-center">
            Cheaper than Google, Facebook, Instagram, and all other social media
            platforms.
          </p>
          <p className="text-gray-600 [#ED00AA] mt-12 mb-4 text-xl">
            For only $2.00/month, you can now place your ads in as many
            countries of your choice.
          </p>

          <form
            onSubmit={handleSubmit}
            className="space-y-6 bg-white shadow-md rounded-xl p-6"
          >
            {/* Selected Country Names Field */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Selected Countries
              </label>
              <textarea
                readOnly
                value={matchedCountries.map((c) => c.name).join(", ")}
                rows={2}
                className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100 text-gray-700"
              />
            </div>

            {/* Standard Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { name: "full_name", label: "Full Name" },
                { name: "last_name", label: "Last Name" },
                { name: "email", label: "Email Address", type: "email" },
                { name: "phone_number", label: "Phone Number", type: "tel" },
                { name: "city", label: "Website URL" },
              ].map(({ name, label, type = "text" }) => (
                <input
                  key={name}
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={label}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition-shadow shadow-md hover:shadow-lg"
                  required
                />
              ))}
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                Select Your Category
              </label>
              <select
                name="category"
                value={formData.category || ""}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg p-3 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 shadow-md hover:shadow-lg"
              >
                <option value="" disabled>
                  Select a category
                </option>
                {[
                  "Health",
                  "Legal",
                  "Education",
                  "Jobs",
                  "Business",
                  "Finance",
                  "Housing",
                  "Family",
                  "Fashion",
                  "Community",
                ].map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Ad Description */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Ad Content Description
              </label>
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                placeholder="Describe your ad content..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 transition-shadow shadow-md hover:shadow-lg"
              ></textarea>
            </div>

            {/* Upload Field */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Upload Your Ad File
              </label>
              <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 bg-gray-50 w-full">
                <Upload className="w-10 h-10 text-gray-400 mb-3" />
                <input
                  type="file"
                  accept="image/*,video/*,application/pdf"
                  className="hidden"
                  id="ad-file"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="ad-file"
                  className="bg-pink-600 text-white py-2 px-4 rounded hover:bg-pink-700 transition"
                >
                  Choose File
                </label>

                {/* File Name */}
                {formData.file && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected file: <strong>{formData.file.name}</strong>
                  </p>
                )}

                {/* Preview Display */}
                {formData.file && formData.file.type.startsWith("image/") && (
                  <img
                    src={URL.createObjectURL(formData.file)}
                    alt="Preview"
                    className="mt-4 rounded-lg shadow max-h-60"
                  />
                )}

                {formData.file && formData.file.type.startsWith("video/") && (
                  <video
                    controls
                    src={URL.createObjectURL(formData.file)}
                    className="mt-4 rounded-lg shadow max-h-60"
                  />
                )}

                {formData.file && formData.file.type === "application/pdf" && (
                  <iframe
                    title="PDF Preview"
                    src={URL.createObjectURL(formData.file)}
                    className="mt-4 w-full h-60 rounded-lg shadow"
                  />
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center pt-4">
              <button
                type="submit"
                className="bg-pink-600 text-white py-2 px-4 rounded hover:bg-pink-700 transition"
              >
                Submit Form
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CategoryAdverPage;
